/**
 * HeroSection Component
 *
 * Reusable hero section with gradient background and SVG pattern overlay.
 * Used consistently across Dashboard and Analytics screens.
 */
import React from "react";
import { Box, Typography, SxProps, Theme } from "@mui/material";
import { motion } from "framer-motion";
import { gradients } from "../utils/theme";

interface HeroSectionProps {
  /** Page title */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Gradient background (default: royalBlue) */
  gradient?: keyof typeof gradients | string;
  /** Additional actions or components to render */
  children?: React.ReactNode;
  /** Additional sx props */
  sx?: SxProps<Theme>;
}

/**
 * SVG Pattern Overlay - Subtle repeating pattern for texture
 */
const svgPattern = `url('data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')`;

/**
 * HeroSection - Reusable hero section with gradient and pattern
 *
 * @example
 * ```tsx
 * <HeroSection
 *   title="Dashboard"
 *   subtitle="Overview & Key Metrics"
 *   gradient="royalBlue"
 * />
 * ```
 */
export const HeroSection: React.FC<HeroSectionProps> = ({
  title,
  subtitle,
  gradient = "royalBlue",
  children,
  sx,
}) => {
  // Get gradient from theme or use custom gradient string
  const gradientValue =
    gradient in gradients
      ? gradients[gradient as keyof typeof gradients]
      : gradient;

  return (
    <Box
      sx={{
        background: gradientValue,
        color: "white",
        pt: { xs: 3, sm: 4 },
        pb: { xs: 4, sm: 5 },
        px: { xs: 2, sm: 3 },
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: svgPattern,
          opacity: 0.3,
          pointerEvents: "none",
        },
        ...sx,
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          px: { xs: 2, sm: 2, md: 1.5 },
          maxWidth: "100%",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Typography
            variant="h3"
            gutterBottom
            fontWeight={700}
            sx={{
              fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
            }}
          >
            {title}
          </Typography>
        </motion.div>
        {subtitle && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          >
            <Typography
              variant="h6"
              sx={{
                opacity: 0.95,
                fontSize: { xs: "1rem", sm: "1.25rem" },
                maxWidth: { xs: "100%", md: "85%" },
              }}
            >
              {subtitle}
            </Typography>
          </motion.div>
        )}
        {children && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
          >
            {children}
          </motion.div>
        )}
      </Box>
    </Box>
  );
};

export default HeroSection;
